<template>
  <el-dialog
      :title="$t('text.importPayeeMatchConfig')"
      :visible.sync="visible"
      width="560px"
      :close-on-click-modal="false"
      @close="onClose"
  >
    <PayeeMatchUsageGuide scene="import"/>
    <div class="import-tip">
      <p>{{ $t('message.payeeMatchImportColumns') }}</p>
      <p>{{ $t('message.payeeMatchImportMultiValueTip') }}</p>
      <p>{{ $t('message.payeeMatchImportEnabledTip') }}</p>
      <p class="import-warning">{{ $t('message.payeeMatchImportTextFormatTip') }}</p>
      <el-button type="text" @click="downloadTemplate">{{ $t('text.downloadTemplate') }}</el-button>
    </div>

    <el-upload
        ref="upload"
        drag
        action=""
        :auto-upload="false"
        :limit="1"
        :file-list="fileList"
        :on-change="onFileChange"
        :on-remove="onFileRemove"
        accept=".xls,.xlsx"
    >
      <i class="el-icon-upload"/>
      <div class="el-upload__text">{{ $t('message.dropOrClickUpload') }}</div>
      <div slot="tip" class="el-upload__tip">{{ $t('message.onlyExcelFiles') }}</div>
    </el-upload>

    <div v-if="result" class="import-result">
      <p>
        {{ $t('text.importResult') }}：
        {{ $t('text.success') }} {{ result.successCount }}，
        {{ $t('text.fail') }} {{ result.failCount }}
        （{{ $t('text.total') }} {{ result.totalRows }}）
      </p>
      <el-table
          v-if="result.errors && result.errors.length"
          :data="result.errors"
          size="mini"
          border
          max-height="200"
      >
        <el-table-column prop="rowNum" :label="$t('text.rowNum')" width="70" align="center"/>
        <el-table-column prop="channelId" :label="$t('text.channelId')" width="100" align="center"/>
        <el-table-column prop="message" :label="$t('text.failReason')" min-width="180"/>
      </el-table>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="visible = false">{{ $t('text.cancel') }}</el-button>
      <el-button type="primary" :loading="importing" :disabled="!selectedFile" @click="submit">
        {{ $t('text.confirm') }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import * as XLSX from 'xlsx';
import PayeeMatchUsageGuide from "./PayeeMatchUsageGuide.vue";

export default {
  name: "PayeeMatchConfigImportDialog",
  components: {
    PayeeMatchUsageGuide
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      importing: false,
      fileList: [],
      selectedFile: null,
      result: null
    };
  },
  computed: {
    visible: {
      get() {
        return this.show;
      },
      set(val) {
        this.$emit("update:show", val);
      }
    }
  },
  methods: {
    onClose() {
      this.fileList = [];
      this.selectedFile = null;
      this.result = null;
      this.importing = false;
    },
    onFileChange(file, fileList) {
      this.fileList = fileList.slice(-1);
      this.selectedFile = file && file.raw ? file.raw : null;
      this.result = null;
    },
    onFileRemove() {
      this.fileList = [];
      this.selectedFile = null;
      this.result = null;
    },
    downloadTemplate() {
      // 文本格式(numFmt @)：避免 Excel 去掉前导 0、把长数字变成科学计数/截断精度
      const headers = ["渠道编号", "比对帐号", "比对名称", "是否启用比对"];
      const sample = [
        "0111",
        "031234567890,031234567891",
        "XXX MERCHANT,XXX Pvt Ltd",
        "是"
      ];
      const PREFILL_ROWS = 500; // 预填空白文本格，用户直接在这些行输入才不会自动变数字
      const ws = {};
      const textCell = (v) => ({t: "s", v: v == null ? "" : String(v), z: "@"});
      headers.forEach((h, c) => {
        ws[XLSX.utils.encode_cell({r: 0, c})] = {t: "s", v: h};
      });
      sample.forEach((v, c) => {
        ws[XLSX.utils.encode_cell({r: 1, c})] = textCell(v);
      });
      for (let r = 2; r <= PREFILL_ROWS; r++) {
        for (let c = 0; c < 4; c++) {
          ws[XLSX.utils.encode_cell({r, c})] = textCell("");
        }
      }
      ws["!ref"] = XLSX.utils.encode_range({
        s: {r: 0, c: 0},
        e: {r: PREFILL_ROWS, c: 3}
      });
      ws["!cols"] = [{wch: 14}, {wch: 40}, {wch: 36}, {wch: 14}];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "payee_match");
      // cellStyles 才会把 z:'@' 写成 Excel 文本格式（numFmtId 49）
      XLSX.writeFile(wb, "payee_match_config_template.xlsx", {cellStyles: true});
    },
    submit() {
      if (!this.selectedFile) {
        this.$message.error(this.$t("message.pleaseSelectFile"));
        return;
      }
      const name = (this.selectedFile.name || "").toLowerCase();
      if (!name.endsWith(".xls") && !name.endsWith(".xlsx")) {
        this.$message.error(this.$t("message.onlyExcelFiles"));
        return;
      }
      const formData = new FormData();
      formData.append("file", this.selectedFile);
      this.importing = true;
      this.$axios
          .post("/routeChannelInfo/payeeMatchConfigImport", formData, {
            transformRequest: [
              function (data, headers) {
                delete headers.post["Content-Type"];
                return data;
              }
            ]
          })
          .then(res => {
            if (res.code === 200) {
              this.result = res.data || {};
              this.$notify({
                title: this.$t("text.prompt"),
                message: res.msg,
                type: this.result.failCount > 0 ? "warning" : "success"
              });
              this.$emit("imported", this.result);
              this.visible = false;
            } else {
              this.$notify.error({
                title: this.$t("text.prompt"),
                message: res.msg
              });
            }
          })
          .finally(() => {
            this.importing = false;
          });
    }
  }
};
</script>

<style scoped>
.import-tip {
  margin-bottom: 12px;
  color: #606266;
  font-size: 13px;
  line-height: 1.6;
}

.import-tip p {
  margin: 0 0 4px;
}

.import-tip .import-warning {
  color: #e6a23c;
  margin-top: 8px;
}

.import-result {
  margin-top: 16px;
}
</style>
